import { useCallback, useLayoutEffect, useMemo, useState, type JSX } from "react";
import _ from "lodash";
import { Pagination } from "@similarweb/ui-components";
import { SWReactTable } from "app/components/React/Table/SWReactTable";
import type { TechnologyItem } from "app/pages/workspace/sales/sub-modules/feed/types/technologies";
import {
    sortSettings,
    TECHNOGRAPHICS_PAGE_TABLE,
} from "app/pages/website-analysis/website-technologies/constants/constants";
import type { SortableField, SortDirection, SortSettingsType } from "./types";
import { StatusFilterValue } from "./types";
import { getTableColumns } from "./columns";
import { filterByStatus, enrichWithStatusAndPeriods } from "./technologyList";
import { PaginationWrapper, TableContainer } from "./styles";

type WebsiteTechnologiesTableProps = {
    keys: string[];
    rawData: TechnologyItem[];
    isLoading: boolean;
    searchString: string;
    selectedCategory: string;
    getSiteImage?(site: string): string;
    selectedSubdomains: string[];
    columnsConfig?: Record<string, unknown>[];
    pageSize?: number;
    selectedSubCategory?: string;
    dataChangeCallback?(data: TechnologyItem[]): TechnologyItem[];
    onPageChange?(newPage: number): void;
    sortingRules?: SortSettingsType;
    statusFilterValue?: StatusFilterValue;
};

export const WebsiteTechnologiesTable = ({
    keys,
    rawData,
    isLoading,
    searchString,
    selectedCategory,
    getSiteImage,
    selectedSubdomains,
    columnsConfig,
    pageSize,
    selectedSubCategory,
    sortingRules = sortSettings,
    dataChangeCallback = _.identity,
    statusFilterValue = StatusFilterValue.InstalledAndRemoved,
    onPageChange,
}: WebsiteTechnologiesTableProps): JSX.Element => {
    const [order, setOrder] = useState(sortingRules);
    const [currentPage, setCurrentPage] = useState(0);
    const itemsPerPage = pageSize || 100;
    const handlePageChange = useCallback(
        (newPage) => {
            setCurrentPage(newPage - 1);
            onPageChange?.(newPage);
        },
        [onPageChange],
    );

    function onSortClickSort(column: { field: SortableField }, sortDirection: SortDirection) {
        const { [column.field]: primarySortField, ...otherSortFields } = sortSettings;
        setOrder({
            [column.field]: sortDirection,
            ...otherSortFields,
        } as SortSettingsType);
    }

    function enrichAndFilterData(): TechnologyItem[] {
        function filterByCategory(data: TechnologyItem[]): TechnologyItem[] {
            if (selectedCategory && selectedCategory !== null) {
                return data.filter(({ category }) => category === selectedCategory);
            }

            if (selectedSubCategory && selectedSubCategory !== null) {
                return data.filter(({ subCategory }) => subCategory === selectedSubCategory);
            }

            return data;
        }

        function filterBySubDomain(technologies: TechnologyItem[]): TechnologyItem[] {
            if (!selectedSubdomains.length) {
                return technologies;
            }

            return technologies.reduce<TechnologyItem[]>((filtered, item) => {
                const filteredUsages = item.usages.filter((usage) => {
                    return selectedSubdomains.includes(usage.site);
                });

                if (filteredUsages.length > 0) {
                    filtered.push({ ...item, usages: filteredUsages });
                }

                return filtered;
            }, []);
        }

        function filterBySearchTerm(data: TechnologyItem[]): TechnologyItem[] {
            if (searchString && searchString.trim()) {
                return data.filter((item) =>
                    Object.values(item)
                        .filter((val) => !_.isNil(val))
                        .some((val) => val.toString().trim().toLowerCase().includes(searchString.trim().toLowerCase())),
                );
            }
            return data;
        }

        return _.flowRight(
            dataChangeCallback,
            filterBySearchTerm,
            filterByCategory,
            filterByStatus(statusFilterValue),
            enrichWithStatusAndPeriods,
            filterBySubDomain,
        )(rawData);
    }

    function sortData(records: TechnologyItem[]) {
        const makeGetValueToSort = (field: SortableField) => (record: TechnologyItem) => {
            const value = record[field];

            if (_.isNil(value)) {
                return "zzzzz";
            }

            if (typeof value === "string") {
                return value.toLowerCase().trim();
            }

            return value;
        };

        const getValueToSortFns = Object.keys(order).map(makeGetValueToSort);

        return _.orderBy(records, getValueToSortFns, Object.values(order));
    }

    const filteredData = useMemo(
        () => enrichAndFilterData(),
        [rawData, searchString, selectedCategory, selectedSubCategory, selectedSubdomains, statusFilterValue],
    );

    const sortedData = useMemo(() => sortData(filteredData), [filteredData, order]);

    useLayoutEffect(() => {
        setCurrentPage(0);
    }, [sortedData]);

    const finalData = useMemo(() => {
        const nextPageIndex = currentPage * itemsPerPage;
        const nextChunk = sortedData.slice(nextPageIndex, nextPageIndex + itemsPerPage);
        return { Records: nextChunk, TotalCount: sortedData.length };
    }, [sortedData, currentPage]);

    const tableOptions = useMemo(
        () => ({
            onSort: onSortClickSort,
            metric: TECHNOGRAPHICS_PAGE_TABLE,
        }),
        [],
    );

    const tableColumns = useMemo(
        () => getTableColumns(keys, order, getSiteImage, selectedSubdomains),
        [keys, order, selectedSubdomains],
    );

    const showPagination = pageSize ? pageSize < sortedData.length : !!sortedData.length;

    const itemsCount = sortedData.length;

    return (
        <TableContainer>
            <SWReactTable
                tableData={finalData}
                tableColumns={columnsConfig || tableColumns}
                tableOptions={tableOptions}
                isLoading={isLoading}
            />
            <div>
                {showPagination ? (
                    <PaginationWrapper>
                        <Pagination
                            page={currentPage + 1}
                            itemsPerPage={itemsPerPage}
                            itemsCount={itemsCount}
                            handlePageChange={handlePageChange}
                            captionPosition="center"
                            hasItemsPerPageSelect={false}
                        />
                    </PaginationWrapper>
                ) : null}
            </div>
        </TableContainer>
    );
};
